if (!customElements.get('itg-article')) {
  customElements.define(
    'itg-article',
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.body = this.querySelector('[data-article-body]');
        this.toc = this.querySelector('[data-article-toc]');
        this.details = this.querySelector('[data-toc-details]');
        this.list = this.querySelector('[data-toc-list]');
        this.current = this.querySelector('[data-toc-current]');
        if (!this.body || !this.toc || !this.list) return;
        this.body.querySelectorAll(':scope > p').forEach((paragraph) => {
          if (paragraph.querySelector('img, video, iframe')) return;
          if (paragraph.textContent.replace(/\u00a0/g, '').trim()) return;
          paragraph.remove();
        });
        this.headings = [...this.body.querySelectorAll('h2, h3')].filter(
          (heading) =>
            !heading.closest('.itg-article-products') &&
            heading.textContent.trim()
        );
        this.list.replaceChildren();
        this.toc.hidden = !this.headings.length;
        if (!this.headings.length) return;

        // Reserve existing IDs before generating any, including IDs outside the article.
        const ids = new Map();
        document
          .querySelectorAll('[id]')
          .forEach((element) =>
            ids.set(element.id, (ids.get(element.id) || 0) + 1)
          );
        this.links = this.headings.map((heading, index) => {
          if (!heading.id || ids.get(heading.id) !== 1) {
            const base = `${this.id || 'article'}-heading-${index + 1}`;
            let id = base;
            let suffix = 2;
            while (ids.has(id)) id = `${base}-${suffix++}`;
            heading.id = id;
            ids.set(id, 1);
          }
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = heading.textContent.trim();
          link.dataset.level = heading.tagName.slice(1);
          item.append(link);
          this.list.append(item);
          return link;
        });
        this.desktop = matchMedia('(min-width: 1150px)');
        const layout = () => {
          this.details.open = this.desktop.matches;
          this.measure();
          this.schedule();
        };
        this.desktop.addEventListener('change', layout, { signal });
        this.list.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest('a');
            const index = this.links.indexOf(link);
            if (
              index < 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey ||
              event.button !== 0
            )
              return;
            event.preventDefault();
            this.initialAnchor = false;
            if (!this.desktop.matches) this.details.open = false;
            const heading = this.headings[index];
            history.pushState(null, '', link.hash);
            this.jump(heading, true);
            if (!heading.hasAttribute('tabindex')) {
              heading.setAttribute('tabindex', '-1');
              heading.addEventListener(
                'blur',
                () => heading.removeAttribute('tabindex'),
                { once: true, signal }
              );
            }
            heading.focus({ preventScroll: true });
          },
          { signal }
        );
        this.details.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && !this.desktop.matches) {
              this.details.open = false;
              this.details.querySelector('summary').focus();
            }
          },
          { signal }
        );
        window.addEventListener('scroll', () => this.schedule(), {
          passive: true,
          signal
        });
        window.addEventListener(
          'resize',
          () => {
            this.measure();
            this.schedule();
          },
          { passive: true, signal }
        );
        window.addEventListener('hashchange', () => this.followHash(), {
          signal
        });
        this.initialAnchor = Boolean(location.hash);
        for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
          window.addEventListener(
            type,
            () => {
              this.initialAnchor = false;
            },
            { passive: true, signal }
          );
        }
        this.observer = new ResizeObserver(() => {
          this.measure();
          if (this.initialAnchor) this.followHash();
          this.schedule();
        });
        this.observer.observe(this.body);
        this.observer.observe(this.toc);
        const header = document.querySelector('.shopify-section--header');
        if (header) this.observer.observe(header);
        layout();
        this.followHash();
        this.update();
      }

      measure() {
        const height = this.desktop.matches
          ? 0
          : this.toc.getBoundingClientRect().height;
        const value = `${Math.ceil(height)}px`;
        if (this.style.getPropertyValue('--itg-toc-offset') !== value)
          this.style.setProperty('--itg-toc-offset', value);
      }

      offset() {
        const sticky = this.desktop.matches
          ? this.querySelector('.itg-article-aside')
          : this.toc;
        return (
          (parseFloat(getComputedStyle(sticky).top) || 0) +
          (this.desktop.matches
            ? 0
            : this.toc.getBoundingClientRect().height + 20)
        );
      }

      jump(heading, smooth = false) {
        window.scrollTo({
          top: Math.max(
            0,
            scrollY + heading.getBoundingClientRect().top - this.offset()
          ),
          behavior:
            smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'smooth'
              : 'instant'
        });
      }

      followHash() {
        let id;
        try {
          id = decodeURIComponent(location.hash.slice(1));
        } catch {
          return;
        }
        const heading = this.headings.find((item) => item.id === id);
        if (heading) this.jump(heading);
      }

      schedule() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        let index = 0;
        const offset = this.offset() + 2;
        this.headings.forEach((heading, i) => {
          if (heading.getBoundingClientRect().top <= offset) index = i;
        });
        if (
          scrollY > 0 &&
          Math.ceil(scrollY + innerHeight) >=
            document.documentElement.scrollHeight
        )
          index = this.headings.length - 1;
        this.links.forEach((link, i) => {
          if (i === index) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        this.current.textContent = this.links[index].textContent;
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }
  );
}
